import { httpClient } from '@/shared/api/httpClient';

export type ChaveConfiguracao =
  | 'termo_impressao_a4'
  | 'termo_impressao_58mm'
  | 'validade_link_acompanhamento_dias'
  | 'captura_tipo_veiculo';

/** Faixa aceita para a validade do link de acompanhamento (dias). */
export const VALIDADE_LINK_MIN_DIAS = 1;
export const VALIDADE_LINK_MAX_DIAS = 30;
/** Sem nada salvo, o link vale 1 dia (o comportamento de antes da opção). */
export const VALIDADE_LINK_PADRAO_DIAS = 1;

/** Dias efetivos a partir do valor salvo (texto); padrão quando vazio/inválido. */
export function diasValidadeLink(valor: string | null | undefined): number {
  const n = Number((valor ?? '').trim());
  if (!Number.isInteger(n) || n < VALIDADE_LINK_MIN_DIAS) return VALIDADE_LINK_PADRAO_DIAS;
  return Math.min(n, VALIDADE_LINK_MAX_DIAS);
}

export function rotuloDias(dias: number): string {
  return dias === 1 ? '1 dia' : `${dias} dias`;
}

/** Desenho das fotos pelo celular: só carro, só moto ou os dois. */
export type PreferenciaTipoVeiculo = 'carro' | 'moto' | 'ambos';

/** Sem nada salvo, só carro — mesmo padrão do servidor. */
export const PREFERENCIA_TIPO_VEICULO_PADRAO: PreferenciaTipoVeiculo = 'carro';

/** Preferência efetiva a partir do valor salvo; padrão quando vazio/inválido. */
export function preferenciaTipoVeiculo(valor: string | null | undefined): PreferenciaTipoVeiculo {
  const t = (valor ?? '').trim();
  return t === 'carro' || t === 'moto' || t === 'ambos' ? t : PREFERENCIA_TIPO_VEICULO_PADRAO;
}

export type ConfiguracaoResposta = {
  chave: ChaveConfiguracao;
  valor: string | null;
};

export type DadosEmpresa = {
  nomeEmpresa: string | null;
  telefone: string | null;
  email: string | null;
  cnpj: string | null;
  inscricaoEstadual: string | null;
  endereco: string | null;
  numero: string | null;
  bairro: string | null;
  cidade: string | null;
  estado: string | null;
  cep: string | null;
};

export type ResultadoReenvioPendentes = {
  clientes: number;
  produtos: number;
  servicos: number;
  colaboradores: number;
  veiculos: number;
  total: number;
  push:
    | { tipo: 'sem-eventos' }
    | { tipo: 'sem-sessao' }
    | { tipo: 'ok'; enviados: number; ignorados: number }
    | { tipo: 'falha'; motivo: string };
};

export const configuracoesApi = {
  obter: (chave: ChaveConfiguracao) =>
    httpClient.get<ConfiguracaoResposta>(`/api/configuracoes/${chave}`),
  salvar: (chave: ChaveConfiguracao, valor: string | null) =>
    httpClient.put<void>(`/api/configuracoes/${chave}`, { valor }),
  obterEmpresa: () =>
    httpClient.get<DadosEmpresa>('/api/configuracoes-empresa'),
  salvarEmpresa: (dados: Partial<DadosEmpresa>) =>
    httpClient.put<DadosEmpresa>('/api/configuracoes-empresa', dados),
  reenviarPendentes: () =>
    httpClient.post<ResultadoReenvioPendentes>('/api/sync/reenviar-pendentes', {}),
  forcarRedump: () =>
    httpClient.post<ResultadoRedump>('/api/sync/forcar-redump', {}),
  conferirSincronizacao: () => httpClient.get<Conferencia>('/api/sync/conferir'),
  baixarTipo: (entidadeTipo: string) =>
    httpClient.post<ResultadoBaixarTipo>('/api/sync/baixar-tipo', { entidadeTipo }),
};

export type ResultadoBaixarTipo =
  | { tipo: 'sem-sessao' }
  | { tipo: 'falha'; motivo: string }
  | { tipo: 'ok'; entidadeTipo: string; registros: number };

export type LinhaConferencia = {
  entidadeTipo: string;
  rotulo: string;
  noServidor: number;
  local: number;
  /** Positivo = falta baixar. Negativo = existe só aqui, ainda não subiu. */
  diferenca: number;
};

export type Conferencia =
  | { tipo: 'sem-sessao' }
  | { tipo: 'falha'; motivo: string }
  | {
      tipo: 'ok';
      linhas: LinhaConferencia[];
      faltando: number;
      soLocal: number;
      totalEventosServidor: number;
      ultimoEventoServidor: string | null;
      cursorLocal: string | null;
      /** O cursor do pull ficou para trás do último evento do servidor. */
      atrasado: boolean;
      geradoEm: string;
    };

export type ResultadoRedump =
  | { tipo: 'sem-sessao' }
  | { tipo: 'falha'; motivo: string }
  | {
      tipo: 'ok';
      entidades: number;
      /** Sempre 0 — o caminho não reexecuta mais histórico. */
      eventosHistoricos: number;
      /** Sempre false — o dump agora é paginado, não trunca. */
      truncado: boolean;
      cursor: string | null;
      /** Caminho da cópia do banco tirada antes de gravar, se deu. */
      copiaSeguranca: string | null;
    };
